import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { HomepageConfig } from '@cng/shared';
import { SmartImage } from '@/components/common/SmartImage';

/** Internal links use the router; tel:, wa.me and absolute URLs must not. */
function isRouterLink(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

function photoLabel(url: string | null | undefined): string {
  if (url?.includes('ai-nail-art-')) return 'Nail art';
  if (url?.includes('ai-facial-')) return 'Facial care';
  if (url?.includes('ai-manicure-')) return 'Manicure';
  if (url?.includes('ai-pedicure-')) return 'Pedicure';
  if (url?.includes('ai-mens-grooming-')) return "Men’s grooming";
  return 'Hair & styling';
}

export function Hero({ hero }: { hero: HomepageConfig['hero'] }) {
  const slides = (hero?.carousel ?? []).filter((m) => m?.url);
  const [active, setActive] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const selected = Math.min(active, Math.max(0, slides.length - 1));
  const [transition, setTransition] = useState<{ from: number; direction: number } | null>(null);
  const busy = useRef(false);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [hidden, setHidden] = useState(false);
  const goTo = useCallback((target: number, direction = 1) => {
    if (busy.current || target === selected || slides.length < 2) return;
    if (!reducedMotion) {
      busy.current = true;
      setTransition({ from: selected, direction });
    }
    setActive(target);
  }, [selected, slides.length, reducedMotion]);
  const move = (step: number) => goTo((selected + step + slides.length) % slides.length, step);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(media.matches);
    const updateVisibility = () => setHidden(document.hidden);
    updateMotion();
    updateVisibility();
    media.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      media.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => {
    if (!transition) return;
    const timer = window.setTimeout(() => { busy.current = false; setTransition(null); }, 650);
    return () => window.clearTimeout(timer);
  }, [transition]);

  useEffect(() => {
    if (slides.length < 2 || paused || hovered || focused || reducedMotion || hidden) return;
    const timer = window.setInterval(() => goTo((selected + 1) % slides.length), 3000);
    return () => window.clearInterval(timer);
  }, [selected, slides.length, paused, hovered, focused, reducedMotion, hidden, goTo]);

  // Warm the next responsive image before its turn so the joined slide stays filled.
  const nextSlide = slides[(selected + 1) % slides.length];
  useEffect(() => {
    if (slides.length < 2 || !nextSlide?.url) return;
    const image = new Image();
    image.sizes = '(max-width: 760px) calc(100vw - 48px), (max-width: 1100px) 42vw, 480px';
    image.srcset = (nextSlide.derivatives ?? []).filter((d) => d.format === 'webp').map((d) => `${d.url} ${d.width}w`).join(', ');
    image.src = nextSlide.url;
  }, [nextSlide?.url, slides.length]);
  const desktopStats = (hero?.stats ?? []).filter((s) => s.enabled).sort((a, b) => a.order - b.order);

  const renderCta = (cta: HomepageConfig['hero']['ctas'][number], index: number) => {
    const className = cta.style === 'primary' ? 'cg9-btn-a' : 'cg9-btn-b';
    // A call button carries a phone glyph on the live site.
    const label = cta.type === 'phone' ? <>&#128222; {cta.label}</> : cta.label;

    if (isRouterLink(cta.href)) {
      return <Link className={className} to={cta.href} key={`${cta.label}-${index}`}>{label}</Link>;
    }
    const external = /^https?:/i.test(cta.href);
    return (
      <a
        className={className}
        href={cta.href}
        key={`${cta.label}-${index}`}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
      >
        {label}
      </a>
    );
  };

  return (
    <div className="cg9-hero">
      {hero?.ghostText ? <div className="cg9-ghost" aria-hidden="true">{hero.ghostText}</div> : null}

      <div className="cg9-inner">
        <div className="cg9-left">
          {hero?.eyebrow ? (
            <div className="cg9-eyebrow">
              <span className="cg9-ebar" aria-hidden="true" />
              {hero.eyebrow}
            </div>
          ) : null}

          <h1 className="cg9-h1">
            {(hero?.headingLines?.length ? hero.headingLines : ['Charlie & Guys Family Salon']).map((line, i, arr) => (
              <span key={line}>
                {line}
                {i < arr.length - 1 ? <br /> : null}
              </span>
            ))}
            {hero?.headingAccent ? <>{' '}<em>{hero.headingAccent}</em></> : null}
          </h1>

          {hero?.description ? (
            <p className="cg9-desc">
              {hero.description}
              {hero.descriptionStrong ? <> <strong>{hero.descriptionStrong}</strong>.</> : null}
            </p>
          ) : null}

          {(hero?.tags ?? []).length > 0 && (
            <div className="cg9-svcs">
              {hero.tags.map((tag) => (
                <span className="cg9-tag" key={tag}>{tag}</span>
              ))}
            </div>
          )}

          {hero?.openPillText ? (
            <div className="cg9-pill">
              <span className="cg9-pdot" aria-hidden="true" />
              {hero.openPillTag ? <span className="cg9-ptag">{hero.openPillTag}</span> : null}
              {hero.openPillText}
            </div>
          ) : null}

          {(hero?.ctas ?? []).length > 0 && (
            <div className="cg9-btns">{hero.ctas.map(renderCta)}</div>
          )}

          {desktopStats.length > 0 && (
            <>
              {/*
                The original renders two stat bars — full labels on desktop, short
                on mobile — and its dividers are siblings of the stats, not
                children, which is what gives the bar its exact height.
              */}
              <div className="cg9-sbar">
                {desktopStats.map((s, i) => (
                  <Fragment key={`d-${s.label}`}>
                    {i > 0 ? <div className="cg9-sdiv" aria-hidden="true" /> : null}
                    <div className="cg9-s">
                      <span className="cg9-sn">{s.value}</span>
                      <span className="cg9-sl">{s.label}</span>
                    </div>
                  </Fragment>
                ))}
              </div>
              <div className="cg9-smob" aria-hidden="true">
                {desktopStats.map((s, i) => (
                  <Fragment key={`m-${s.label}`}>
                    {i > 0 ? <div className="cg9-sdiv" /> : null}
                    <div className="cg9-s">
                      <span className="cg9-sn">{s.value}</span>
                      <span className="cg9-sl">{s.shortLabel || s.label}</span>
                    </div>
                  </Fragment>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="cg9-right" role="region" aria-roledescription="carousel" aria-label="Salon services inspiration"
          onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
          onFocusCapture={() => setFocused(true)}
          onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
          onKeyDown={(event) => {
            if (slides.length < 2) return;
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
              event.preventDefault();
              move(event.key === 'ArrowRight' ? 1 : -1);
            }
          }}>
          {slides.length > 0 && (
            <div
              className="cg9-frame"
              onTouchStart={(event) => { touchStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }; }}
              onTouchCancel={() => { touchStart.current = null; }}
              onTouchEnd={(event) => {
                const start = touchStart.current;
                touchStart.current = null;
                if (!start || slides.length < 2) return;
                const dx = event.changedTouches[0].clientX - start.x;
                const dy = event.changedTouches[0].clientY - start.y;
                if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) move(dx < 0 ? 1 : -1);
              }}
            >
              {slides.map((slide, i) => i === selected || i === transition?.from ? (
                <div className={`cg9-slide on${transition ? i === selected ? ' cg9-entering' : ' cg9-exiting' : ''}${transition?.direction === -1 ? ' cg9-backward' : ''}`} aria-hidden={i !== selected} key={slide.url ?? i} role="group" aria-roledescription="slide" aria-label={`${photoLabel(slide.url)}, ${i + 1} of ${slides.length}`}>
                  <SmartImage
                    media={slide}
                    alt={slide.alt || 'Salon styling from Charlie & Guys'}
                    /* Only the active and outgoing images are mounted during motion. */
                    loading="eager"
                    fetchPriority={i === 0 ? 'high' : undefined}
                    sizes="(max-width: 760px) calc(100vw - 48px), (max-width: 1100px) 42vw, 480px"
                  />
                </div>
              ) : null)}
            </div>
          )}
          {slides.length > 1 && (
            <div className="cg9-carousel-controls">
              <div className="cg9-carousel-nav">
                <button type="button" className="cg9-carousel-arrow" aria-label="Previous service image" onClick={() => move(-1)}><span aria-hidden="true">&#8592;</span></button>
                <p className="cg9-carousel-status" role="status" aria-live={paused || hovered || focused || reducedMotion ? 'polite' : 'off'} aria-atomic="true">{photoLabel(slides[selected]?.url)}<span>{selected + 1} / {slides.length}</span></p>
                <button type="button" className="cg9-carousel-arrow" aria-label="Next service image" onClick={() => move(1)}><span aria-hidden="true">&#8594;</span></button>
              </div>
              <button type="button" className="cg9-carousel-pause" onClick={() => setPaused(!paused)} aria-pressed={paused}>
                {paused ? 'Resume slideshow' : 'Pause slideshow'}
              </button>
                <div className="cg9-dots" role="group" aria-label="Choose a service image">
                  {slides.map((slide, i) => (
                    <button
                      type="button"
                      aria-pressed={i === selected}
                      aria-label={`Show ${photoLabel(slide.url)} image, ${i + 1} of ${slides.length}`}
                      className={`cg9-dot${i === selected ? ' on' : ''}`}
                      key={`dot-${slide.url ?? i}`}
                      onClick={() => goTo(i)}
                    />
                  ))}
                </div>
            </div>
          )}
          {slides.length > 0 && !slides[selected]?.url?.includes('/campaign/') ? <p className="hero-photo-caption">From our salon gallery - Hair, colour &amp; everyday confidence</p> : null}
        </div>
      </div>
    </div>
  );
}
